import { memo, useState } from "react"
import { TopBarNavigation } from "../../navigation/TopBarNavigation"
import {
  Alert,
  Button,
  Link,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material"
import { useSettings } from "../../settings/useSettings"
import { links } from "@oboku/shared"
import { setupSecretDialogSignal } from "../../secrets/SetupSecretDialog"
import { useSecrets } from "../../secrets/useSecrets"
import { useDecryptedSecret } from "../../secrets/useDecryptedSecret"
import { useSecret } from "../../secrets/useSecret"
import { KeyRounded } from "@mui/icons-material"
import { SecretActionDrawer } from "../../secrets/SecretActionDrawer"
import { useUnlockMasterKey } from "../../secrets/useUnlockMasterKey"
import { PageContainer } from "../../common/PageContainer"
import { PageSection } from "../../common/PageSection"
import { Page } from "../../common/Page"

const SecretListItem = memo(function SecretListItem({
  id,
  masterKey,
  onClick,
}: {
  id: string
  masterKey?: string
  onClick: () => void
}) {
  const { data: secret } = useSecret(id)
  const { data: decryptedSecret, isPending } = useDecryptedSecret({
    id,
    masterKey,
  })
  const secretValue = !masterKey
    ? "***********"
    : isPending
      ? "Decrypting..."
      : (decryptedSecret ?? "Unable to decrypt")

  return (
    <ListItemButton onClick={onClick}>
      <ListItemIcon>
        <KeyRounded />
      </ListItemIcon>
      <ListItemText primary={secret?.name} secondary={secretValue} />
    </ListItemButton>
  )
})

export const SecretsScreen = memo(function SecretsScreen() {
  const { data: accountSettings } = useSettings()
  const { data: secrets } = useSecrets()
  const { masterKey, unlockMasterKey } = useUnlockMasterKey()
  const hasMasterPassword = !!accountSettings?.masterEncryptionKey
  const [drawerOpenWith, setDrawerOpenWith] = useState<string | undefined>(
    undefined,
  )

  const handleAddSecret = () => {
    if (masterKey) {
      setupSecretDialogSignal.update({
        openWith: true,
        masterKey,
      })
    } else {
      unlockMasterKey(undefined, {
        onSuccess: (key) => {
          setupSecretDialogSignal.update({
            openWith: true,
            masterKey: key,
          })
        },
      })
    }
  }

  return (
    <>
      <Page bottomGutter={false}>
        <TopBarNavigation title={"Secrets"} />
        <PageContainer>
          <Stack
            sx={{
              gap: 1,
              mt: 2,
            }}
          >
            <Alert severity="info" variant="standard">
              Learn more about secrets{" "}
              <Link href={links.documentationSecrets}>here</Link>
            </Alert>
            {!hasMasterPassword && (
              <Alert severity="warning">
                You need to initialize your Master Password to use secrets
              </Alert>
            )}
            <Button
              disabled={!hasMasterPassword || !!masterKey}
              onClick={unlockMasterKey}
            >
              {masterKey ? "Unlocked" : "Unlock"}
            </Button>
            <Button
              disabled={!hasMasterPassword}
              onClick={handleAddSecret}
              variant="contained"
            >
              Add a secret
            </Button>
          </Stack>
        </PageContainer>
        <PageContainer disableGutters>
          <PageSection>
            {secrets?.map((secret) => (
              <SecretListItem
                key={secret._id}
                id={secret._id}
                masterKey={masterKey}
                onClick={() => setDrawerOpenWith(secret._id)}
              />
            ))}
          </PageSection>
        </PageContainer>
      </Page>
      <SecretActionDrawer
        openWidth={drawerOpenWith}
        onClose={() => setDrawerOpenWith(undefined)}
        masterKey={masterKey}
      />
    </>
  )
})
